import React, { useState, useEffect, useCallback } from 'react';
import storeProductsService from '../../services/storeproducts.service';
import merchantOrdersService from '../../services/merchantorders.service';
import ProductSearch from './ProductSearch';
import QuantityModal from './QuantityModal';
import NewProductModal from './NewProductModal';
import OrderCart from './OrderCart';
import MyOrdersList from './MyOrdersList';
import { SkeletonNewOrderLayout } from './Skeleton';
import styles from './StoreOrdering.module.css';

// Icons
const ChevronIcon = ({ isOpen }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
  </svg>
);

/**
 * StoreOrdering - Single-page ordering with visual hierarchy
 * New order section (prominent) + Recent orders section (muted, collapsible)
 */
const StoreOrdering = ({ storeName = 'Store' }) => {
  // Recent orders expand state
  const [showAllOrders, setShowAllOrders] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);

  // Cart state
  const [cartItems, setCartItems] = useState([]);

  // Editing order state (null = creating new, order object = editing existing)
  const [editingOrder, setEditingOrder] = useState(null);

  // Modal states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [showNewProductModal, setShowNewProductModal] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load products
  useEffect(() => {
    const unsubscribe = storeProductsService.subscribeProducts((loadedProducts) => {
      setProducts(loadedProducts);
      setIsLoadingProducts(false);
    });

    return () => unsubscribe();
  }, []);

  // Load orders
  useEffect(() => {
    const unsubscribe = merchantOrdersService.subscribeOrders((loadedOrders) => {
      setOrders(loadedOrders);
      setIsLoadingOrders(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle product selection from search
  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    setEditingCartItem(null);
  }, []);

  // Handle quantity confirmation
  const handleQuantityConfirm = useCallback((product, quantity) => {
    const newItem = {
      productId: product.id,
      productName: product.name,
      quantity,
      orderUnit: product.orderUnit,
      pricingUnit: product.pricingUnit,
      pricePerPricingUnit: product.pricePerPricingUnit,
      estimatedConversion: product.estimatedConversion,
      unitMeasure: product.unitMeasure,
      estimatedAmount: product.getEstimatedAmount(quantity),
      estimatedTotal: product.getEstimatedCost(quantity),
      isEstimate: product.hasDualUnits(),
    };

    if (editingCartItem !== null) {
      // Editing existing cart item
      setCartItems(prev => {
        const updated = [...prev];
        updated[editingCartItem] = newItem;
        return updated;
      });
    } else {
      // Adding new item
      setCartItems(prev => [...prev, newItem]);
    }

    setSelectedProduct(null);
    setEditingCartItem(null);
  }, [editingCartItem]);

  // Handle cart item click (for editing)
  const handleCartItemClick = useCallback((item) => {
    const product = products.find(p => p.id === item.productId);
    if (product) {
      const index = cartItems.findIndex(i => i.productId === item.productId);
      setEditingCartItem(index);
      setSelectedProduct(product);
    }
  }, [products, cartItems]);

  // Handle remove item from cart
  const handleRemoveItem = useCallback((index) => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle new product creation
  const handleProductCreated = useCallback(async (productData) => {
    const newProduct = await storeProductsService.addProduct(productData);
    setShowNewProductModal(false);
    // Automatically open quantity modal for the new product
    setSelectedProduct(newProduct);
  }, []);

  // Handle order submission (create or update)
  const handleSubmitOrder = useCallback(async () => {
    if (cartItems.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (editingOrder) {
        // Update existing order
        const total = cartItems.reduce((sum, item) => sum + (item.estimatedTotal || 0), 0);
        await merchantOrdersService.updateOrder(editingOrder.id, {
          items: cartItems,
          total,
        });
        alert('Orden actualizada.');
      } else {
        // Create new order
        await merchantOrdersService.createOrder(
          null, // No merchantId
          'Store Products', // Generic merchant name
          cartItems,
          '' // No notes
        );
        alert('Orden enviada.');
      }

      // Clear cart and editing state
      setCartItems([]);
      setEditingOrder(null);
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Error al guardar la orden. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  }, [cartItems, isSubmitting, editingOrder]);

  // Handle edit order from history
  const handleEditOrder = useCallback((order) => {
    // Load order items into cart
    setCartItems(order.items);
    setEditingOrder(order);
    // Scroll to top to show the cart
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Cancel editing and clear cart
  const handleCancelEdit = useCallback(() => {
    setCartItems([]);
    setEditingOrder(null);
  }, []);

  // Close modals
  const closeQuantityModal = useCallback(() => {
    setSelectedProduct(null);
    setEditingCartItem(null);
  }, []);

  return (
    <div className={styles['store-ordering-container']}>
      {/* NEW ORDER SECTION - Prominent, always visible */}
      <section className={styles['new-order-section']}>
        {isLoadingProducts ? (
          <SkeletonNewOrderLayout />
        ) : (
          <div className={styles['new-order-layout']}>
            <div className={styles['products-panel']}>
              <ProductSearch
                products={products}
                onProductSelect={handleProductSelect}
                onAddNewClick={() => setShowNewProductModal(true)}
                cartItems={cartItems}
              />
            </div>

            <div className={styles['cart-panel']}>
              <OrderCart
                items={cartItems}
                onItemClick={handleCartItemClick}
                onRemoveItem={handleRemoveItem}
                onSubmitOrder={handleSubmitOrder}
                isSubmitting={isSubmitting}
                isEditing={!!editingOrder}
                onCancelEdit={handleCancelEdit}
              />
            </div>
          </div>
        )}
      </section>

      {/* RECENT ORDERS - Simple collapsible */}
      {!isLoadingOrders && orders.length > 0 && (
        <button
          className={styles['recent-orders-toggle']}
          onClick={() => setShowAllOrders(!showAllOrders)}
        >
          <span>{showAllOrders ? 'Ocultar' : 'Ver'} Pedidos Recientes ({orders.length})</span>
          <ChevronIcon isOpen={showAllOrders} />
        </button>
      )}

      {showAllOrders && (
        <section className={styles['recent-orders-section']}>
          <MyOrdersList
            orders={orders}
            onEditOrder={handleEditOrder}
          />
        </section>
      )}

      {/* Quantity Modal */}
      {selectedProduct && (
        <QuantityModal
          product={selectedProduct}
          onConfirm={handleQuantityConfirm}
          onClose={closeQuantityModal}
          initialQuantity={
            editingCartItem !== null
              ? cartItems[editingCartItem]?.quantity || 0
              : 0
          }
        />
      )}

      {/* New Product Modal */}
      {showNewProductModal && (
        <NewProductModal
          onClose={() => setShowNewProductModal(false)}
          onProductCreated={handleProductCreated}
        />
      )}
    </div>
  );
};

export default StoreOrdering;
